import type { VendorBillingDetails } from './PropertyStep/utils';

export type FlashOfferProperty = App.Property & {
  _embedded?: {
    room_types?: Array<App.RoomType>;
  };
  category?: string | null;
};

export type AppliedFlashRoomRate = {
  ratePlanId: string;
  ratePlanName?: string;
  roomName: string;
  roomRateId: string;
  roomTypeId: string;
};

export interface SetUpFlashOfferSectionProps {
  disabled?: boolean;
  onPropertiesRefresh?: (property?: FlashOfferProperty, roomTypes?: Array<App.RoomType>) => Promise<void>;
  properties?: Array<FlashOfferProperty>;
  vendor?: (App.Vendor & VendorBillingDetails) | null;
  vendorId: string;
}

export interface SubmissionAttemptFailure {
  /** Packages refused before any write because flexible nights is on with no max-nights value. */
  flexibleNightsBlockedCount: number;
  /** How many packages failed purely on room-name matching, rather than on a rejected write. */
  packageUnmatchedCount: number;
  /** Packages whose write the backend rejected for some other reason. */
  updateFailurePackageIds: Array<string>;
}

/**
 * Every wizard step hook implements this so the Submission review can ask "is your last failure
 * something the CM can act on here?" without knowing how each step decides that -- the decision
 * stays with the step that owns the relevant draft/domain state, not a central switch that has to
 * be taught about every step's internals as new failure modes are added.
 */
export interface SubmissionFailureActionabilityCheck {
  isSubmissionFailureActionable(failure: SubmissionAttemptFailure): boolean;
}

/** Most steps are always actionable: going back always offers a real action (edit the draft,
 * then retry), even when the backend's error message doesn't cleanly say what to fix. */
export const alwaysActionable: SubmissionFailureActionabilityCheck['isSubmissionFailureActionable'] = () => true;

/**
 * Every wizard step hook implements this too, for the same reason: a failed submission always
 * means everything created so far just got rolled back, but a step's own "already created/
 * applied" markers (a real id stashed on a draft, a `hasApplied`-style flag) don't clear
 * themselves -- nothing else does. Left alone, a CM using "Go to step" to fix one thing finds an
 * unrelated step locked read-only, as if a resource that was just deleted still exists. The
 * Submission step calls this on every step after any failure, unconditionally; each step decides
 * for itself whether it has anything to clear.
 */
export interface SubmissionFailureRecovery {
  resetAfterFailedSubmission(): void;
}

/** Steps with nothing a submission attempt could have marked as "created" -- Property is
 * deliberately kept regardless of failure, and Taxes & Fees has no per-step created/applied lock
 * to begin with -- have nothing to reset. */
export const noResetNeeded: SubmissionFailureRecovery['resetAfterFailedSubmission'] = () => {};
